import { DataSource } from "@/store/data-source"
import {
	clearAllPreviews,
	deleteProject_deprecated,
	deleteProjectFileHandle,
	fetchPreviews,
	fetchProject,
	fetchProjectFileHandle,
	fetchProjects_deprecated,
	fetchSyncedPreviews,
	ProjectRecord,
	removePreviewUrl,
	storeProject,
	storeProjectFileHandle,
} from "@/storage/indexeddb"
import { CreateProjectRequestDTO, ProjectDTO } from "@/api/dto/projects"
import { PNApiOperations } from "@/api/operations"
import { fetchAllPages } from "@/api/paginate"
import { PreviewService } from "../previews/previews-service"
import { queryClient } from "@/api/constants"
import { isDefined } from "@/utils/object"
import { Sentry } from "@/telemetry/sentry"
import { ensureSampleProjectMounted, getCachedSampleProjectHandle } from "./sample-project-loader"
import { MainMaskCache } from "@/storage/mask-cache"
import {
	DisplayGroup,
	PhotoFlagStatus,
	PhotoTag,
	ProjectCullingSettings,
	Rating,
	SortOption,
	Visibility,
} from "@/lib/catalog/types"
import { projectIsDeleted } from "@/api/supression"
import { ColorTag } from "@/utils/exif"
import { trackProjectCreatedEvent } from "./project-created-tracking"

//Stored in BE, used to show info in project list
export interface ProjectItem {
	eventStartedAt: Date
	name: string
	photoCount?: number
	sortOption?: SortOption
	previewThumbnailPhotoIds?: string[]

	styleId: string | null
	needsSetup?: boolean
	isManual?: boolean
	isCulling?: boolean
	previewThumbnailUrls?: string[]

	// Not manually set
	id: string
	createdAt: Date
	updatedAt: Date
	source: DataSource
	orderScore: number

	// Not sycnced/supported in BE
	handle?: FileSystemDirectoryHandle
	selectedTags?: PhotoTag[]
	suggestedKeeperCount?: number
	cullingSettings?: ProjectCullingSettings

	ratingFilters?: Rating[]
	pathFilters?: string[]
	colorFilters?: ColorTag[]
	cameraFilters?: string[]
	flagFilters?: PhotoFlagStatus[]
	fileFormatFilters?: string[]
	visibilityFilters?: Visibility[]
	isExpressSetup?: boolean
	displayGroup?: DisplayGroup

	publishCollectionId?: string
	publishGalleryId?: string

	isSampleProject?: boolean
}

//Should keep in sync when we update ProjectItem
export const getUpdatableProjectFields = (updates: Partial<ProjectItem>) => {
	return Object.fromEntries(
		Object.entries(updates).filter(([key, _]) =>
			[
				"eventStartedAt",
				"name",
				"photoCount",
				"sortOption",
				"previewThumbnailPhotoIds",

				"styleId",
				"needsSetup",
				"isManual",
				"isCulling",
				"previewThumbnailUrls",
			].includes(key),
		),
	)
}
export interface ProjectGrouping {
	title: string
	data: ProjectItem[]
}

export class ProjectService {
	static readonly prefetchProjects = (): void => {
		queryClient
			.prefetchQuery({
				queryKey: ["projects"],
				queryFn: ProjectService.listProjects,
				staleTime: 60000,
			})
			.catch(Sentry.captureException)
	}

	static listProjects = async () => {
		const projects: ProjectItem[] = []
		const backendProjects = await fetchAllPages(PNApiOperations.listProjects)
		for (const dto of backendProjects) {
			projects.push(await ProjectService.dtoToProject(dto))
		}

		const indexedDBProjects = await fetchProjects_deprecated()
		for (const record of indexedDBProjects) {
			projects.push(await ProjectService.recordToProject(record))
		}

		attachSampleProjectHandle(projects)

		projects.sort((a, b) => b.orderScore - a.orderScore)
		return projects
	}

	static createProject = async (project: CreateProjectRequestDTO) => {
		const { handle, ...body } = project
		if (!body.previewThumbnailUrls && body.projectId) {
			// Reusing a project ID means that previews may be in indexedDB
			body.previewThumbnailUrls =
				await ProjectService.generateNewPreviewThumbnailUrlsFromIndexedDb(body.projectId)
		}
		const dto = await PNApiOperations.createProject(body)
		const projectItem = await ProjectService.dtoToProject(dto, true)
		if (handle) {
			projectItem.handle = handle
			await storeProjectFileHandle({ id: projectItem.id, handle })
		}
		trackProjectCreatedEvent({ id: projectItem.id, totalCount: dto.photoCount })
		return projectItem
	}

	static updateProject = async (
		options: {
			id: string
			source: DataSource
		},
		mutations: Partial<ProjectItem>,
	) => {
		if (!options.id || !options.source) {
			throw new Error("Cannot update project without a target ID or source.")
		}

		if (options.source === DataSource.Backend) {
			if (mutations.handle) {
				await storeProjectFileHandle({ id: options.id, handle: mutations.handle })
				delete mutations.handle
			}
			try {
				const dto = await PNApiOperations.updateProject(options.id, mutations)
				return ProjectService.dtoToProject(dto)
			} catch (err: any) {
				if (
					err &&
					err.response &&
					err.response.status === 404 &&
					projectIsDeleted(options.id)
				) {
					return undefined
				} else {
					throw err
				}
			}
		} else {
			const existingProjectRecord = await fetchProject(options.id)
			if (!existingProjectRecord) {
				throw new Error(
					`Trying to update an indexedDB project that doesn't exist with id ${options.id}`,
				)
			}
			for (const key in mutations) {
				if (mutations[key] !== undefined) {
					if (key === "orderScore" && mutations.orderScore) {
						existingProjectRecord.createdAt = new Date(
							Math.floor(mutations.orderScore * 1000),
						)
					} else if (key === "eventStartedAt" && mutations.eventStartedAt) {
						existingProjectRecord.createdAt = mutations.eventStartedAt
					} else {
						existingProjectRecord[key] = mutations[key]
					}
				}
			}
			await storeProject(existingProjectRecord)
			return ProjectService.recordToProject(existingProjectRecord)
		}
	}

	static deleteProject = async (options: { id: string; source: DataSource }) => {
		if (!options.id) {
			throw new Error("Cannot delete project")
		}
		if (options.source === DataSource.Backend) {
			await PNApiOperations.deleteProject(options.id)
			await ProjectService.removeAllPreviews(options.id)
			await deleteProjectFileHandle(options.id)
		}
		await deleteProject_deprecated({ id: options.id })
		await MainMaskCache.deleteProject(options.id)
	}

	static removeAllPreviews = async (projectId: string) => {
		await clearAllPreviews(projectId)
	}

	static removeSyncedPreviews = async (projectId: string) => {
		const syncedPreviews = await fetchSyncedPreviews(projectId)
		const actionQueue: Promise<string>[] = []
		for (const preview of syncedPreviews) {
			if (preview.cloudUrl || preview.cloudSyncStatus) {
				actionQueue.push(removePreviewUrl(preview))
			}
			await Promise.allSettled(actionQueue)
		}
	}

	static recordToProject = async (record: ProjectRecord): Promise<ProjectItem> => ({
		id: record.id,
		createdAt: record.createdAt,
		updatedAt: record.updatedAt,
		eventStartedAt: record.createdAt,
		name: record.name,
		orderScore: record.createdAt.getTime() / 1000,
		photoCount: record.photoCount,
		styleId: record.styleId ?? null,
		sortOption: record.sortOption ?? undefined,
		needsSetup: record.needsSetup ?? undefined,
		isManual: record.isManual ?? undefined,
		isCulling: record.isCulling ?? undefined,
		previewThumbnailUrls: await ProjectService.fetchIndexedDBPreviews(record.id),
		source: DataSource.IndexedDB,
		handle: record.handle,
	})

	static dtoToProject = async (
		dto: ProjectDTO,
		isInitial: boolean = false,
	): Promise<ProjectItem> => {
		const handle = (await fetchProjectFileHandle(dto.projectId))?.handle

		if (isInitial) {
			return {
				id: dto.projectId,
				createdAt: new Date(dto.createdAt),
				updatedAt: new Date(dto.updatedAt),
				eventStartedAt: new Date(dto.eventStartedAt),
				name: dto.name,
				orderScore: dto.orderScore,
				source: DataSource.Backend,
				styleId: null,
				handle,
			}
		}

		return {
			id: dto.projectId,
			createdAt: new Date(dto.createdAt),
			updatedAt: new Date(dto.updatedAt),
			eventStartedAt: new Date(dto.eventStartedAt),
			name: dto.name,
			orderScore: dto.orderScore,
			photoCount: dto.photoCount,
			styleId: dto.styleId,
			sortOption: dto.sortOption,
			needsSetup: dto.needsSetup,
			isManual: dto.isManual,
			isCulling: dto.isCulling,
			previewThumbnailPhotoIds: dto.previewThumbnailPhotoIds ?? [],
			previewThumbnailUrls: dto.previewThumbnailUrls ?? [],
			source: DataSource.Backend,
			handle,
			isSampleProject: dto.isSampleProject ?? undefined,
		}
	}

	static fetchIndexedDBPreviews = async (projectId: string) => {
		const previewsRecords = await fetchPreviews(projectId, 4)
		return previewsRecords.map((record) => URL.createObjectURL(record.data))
	}

	static generateNewPreviewThumbnailUrlsFromIndexedDb = async (
		projectId: string,
	): Promise<string[] | undefined> => {
		await ProjectService.removeSyncedPreviews(projectId)
		const previews = await fetchPreviews(projectId, 4)
		if (previews.length > 0) {
			await PreviewService.uploadPreviews(previews)
		}
		const previewThumbnailUrls: string[] = previews
			.map((preview) => preview.cloudUrl)
			.filter(isDefined)

		if (previewThumbnailUrls.length > 0) {
			return previewThumbnailUrls
		} else {
			return undefined
		}
	}
}

// Ensure the synthetic sample project points at the OPFS directory holding the seeded payload.
function attachSampleProjectHandle(projects: ProjectItem[]) {
	const sampleProject = projects.find((project) => project.isSampleProject)
	if (!sampleProject || sampleProject.handle) {
		return
	}

	const cached = getCachedSampleProjectHandle(sampleProject.id)
	if (cached) {
		console.info("[SampleProject] Using cached handle for project list")
		sampleProject.handle = augmentHandleWithRelativePath(cached)
		return
	}

	console.info("[SampleProject] Mounting sample project for project list")
	void ensureSampleProjectMounted(sampleProject.id)
		.then((handle) => {
			console.info("[SampleProject] Sample project mounted for project list")
			sampleProject.handle = augmentHandleWithRelativePath(handle)
		})
		.catch((error) => {
			console.error("Failed to mount sample project", error)
		})
}

function augmentHandleWithRelativePath(
	handle: FileSystemDirectoryHandle,
): FileSystemDirectoryHandle {
	if (!("relativePathSegments" in handle)) {
		Object.defineProperty(handle, "relativePathSegments", {
			value: [] as string[],
			writable: false,
			configurable: false,
		})
	}
	return handle
}
